import { useState, useEffect } from "react";
import { getDownscaledFile } from "../api/documentUpload";
import defaultUserAvatar from "../assets/user.png";

// In-memory cache to share loaded avatar blob URLs across components
const avatarCache = new Map();

export const evictAvatarCache = (docId) => {
  if (docId && avatarCache.has(docId)) {
    const oldUrl = avatarCache.get(docId);
    if (oldUrl && oldUrl.startsWith("blob:")) {
      URL.revokeObjectURL(oldUrl);
    }
    avatarCache.delete(docId);
  }
};

export default function useProfilePicture(docId) {
  const [avatarUrl, setAvatarUrl] = useState(() => {
    if (!docId) return defaultUserAvatar;
    return avatarCache.get(docId) || null;
  });

  useEffect(() => {
    if (!docId) {
      setAvatarUrl(defaultUserAvatar);
      return;
    }

    if (avatarCache.has(docId)) {
      setAvatarUrl(avatarCache.get(docId));
      return;
    }

    let isMounted = true;
    getDownscaledFile(docId)
      .then((blob) => {
        if (blob && isMounted) {
          const url = URL.createObjectURL(blob);
          avatarCache.set(docId, url);
          setAvatarUrl(url);
        } else if (!blob && isMounted) {
          setAvatarUrl(defaultUserAvatar);
        }
      })
      .catch((err) => {
        console.error("Failed to load profile picture blob:", err);
        if (isMounted) {
          setAvatarUrl(defaultUserAvatar);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [docId]);

  return avatarUrl || defaultUserAvatar;
}
